import { AlbumFile, buildOpenedImageCaption, formatFileDateTime, prepareForLightbox } from '@/models/albumFile.ts'
import Lightbox from 'yet-another-react-lightbox'
import { useEffect, useMemo, useRef, useState } from 'react'
import Video from "yet-another-react-lightbox/plugins/video";
import {
  IconCheck,
  IconCircleX,
  IconDownload,
  IconHeart,
  IconHeartFilled,
  IconMessage,
  IconPencil,
  IconShare2,
  IconTrash,
  IconX
} from '@tabler/icons-react';
import { hasPermissionsDeleteComment, hasPermissionsDeleteFile } from '@/controller/permissions.ts'
import { useProfile } from '@/stores/useAuth.ts'
import { Input, Loading, Modal } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { useClient } from '@/stores/useClient.ts'
import { useUploadQueue } from '@/stores/useUploadQueue.ts'
import { useDrives } from '@/stores/useDrives.ts'
import { useFilesContext } from '@/contexts/filesContext.tsx'
import { captureException } from '@/controller/sentryHelper.ts'
import { AlbumFileComment } from '@/models/albumFileComment.ts'
import { HeyProfile } from '@/models/profile.ts'
import { HeyDrive, OpenedImageCaption } from '@/models/drive.ts'
import { DownloadOptions, resolveDownloadQualities, resolveSingleFileQuality } from '@/models/downloadOptions.ts'
import { Inline, Zoom } from 'yet-another-react-lightbox/plugins'
import { Link } from 'react-router-dom'
import { shareDownloadedFileByUrl, shareDownloadURL } from '@/controller/updateHelper.ts'
import { saveAs } from '@/controller/fileSaver.ts'
import { useTranslation } from 'react-i18next'
import { FileSelectionType } from '@/hooks/useFileSelection.tsx'
import { useTrackStat } from '@/contexts/statsScopeContext.tsx'


enum ShareDownloadDataDecision {
  USE_ORIGINAL,
  USE_DETAIL,
  TOO_LARGE_USE_URL,
}

type ShareDownloadData = {
  canShare: false
} | {
  canShare: true
  decision: ShareDownloadDataDecision
}

const NO_SHARE: ShareDownloadData = {canShare: false}
const SHARE_DOWNLOAD_LIMIT = 1024*1024*25

export default function FilesSlideshow(
  { files, selectedFileIndex, onSelectFileIndex, updateFile: updateFileProp, shareMode, showAuthorInfo = true, fileSelection, selectionAsLike, selectionAlbumId, openedImageCaption, downloadOptions }:
    { files: Array<AlbumFile>, selectedFileIndex: number | null, onSelectFileIndex: (fileIndex: number | null) => void, updateFile?: (file: AlbumFile) => void, shareMode?: boolean, showAuthorInfo?: boolean, fileSelection?: FileSelectionType, selectionAsLike?: boolean, selectionAlbumId?: string, openedImageCaption?: OpenedImageCaption | null, downloadOptions?: DownloadOptions | null }
) {
  const { profile } = useProfile()
  const drivesContext = useDrives()
  const currentDrive = drivesContext?.currentDrive || null
  const track = useTrackStat()
  const slides = useMemo(() => {
    return files.map(file => prepareForLightbox(file, currentDrive))
  }, [files, currentDrive])
  const deleteModalRef = useRef<HTMLDialogElement>(null)
  const commentModalRef = useRef<HTMLDialogElement>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isLiking, setIsLiking] = useState(false)
  const { deleteFile, like, unlike, comment, fileDescription } = useClient()
  const { addDeletedFiles } = useUploadQueue()
  const filesContext = useFilesContext()
  const updateFile = updateFileProp || filesContext?.updateFile || (() => {})
  const [commentContent, setCommentContent] = useState('')
  const [isCommenting, setIsCommenting] = useState(false)
  const [isDescribing, setIsDescribing] = useState(false)
  const [descriptionContent, setDescriptionContent] = useState('')
  const [isEditingDescription, setIsEditingDescription] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [isSharing, setIsSharing] = useState(false)
  const [isShowiOSDownloadHint, setIsShowiOSDownloadHint] = useState(false)
  const { t } = useTranslation()

  useEffect(() => {
    // 1. Define the function that will be called on keydown
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedFileIndex != null && e.key === 'Escape') {
        onSelectFileIndex(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedFileIndex]);


  if (selectedFileIndex == null) {
    return <></>
  }

  const currentFile = files[selectedFileIndex]

  // Happens is you delete something
  if (!currentFile) {
    return <></>
  }


  function onDeleteClick() {
    deleteModalRef.current?.showModal()

  }
  async function onDeleteConfirm() {
    setIsDeleting(true)
    try {
      const file = files[selectedFileIndex!]
      await deleteFile(file)
      addDeletedFiles([file])
      deleteModalRef.current?.close()
    } catch (e) {
      captureException(e, 'deleting file')
      console.error(e)
      alert('Error deleting file: ' + JSON.stringify(e))
    } finally {
      setIsDeleting(false)
    }
  }

  async function onLikeClick() {
    if (isLiking) {
      return
    }
    const file = files[selectedFileIndex!]
    if (file.is_demo) {
      return alert(t('common.onlyInFullVersion'))
    }

    setIsLiking(true)
    try {
      let updatedAlbumFileRes
      if (yourLike) {
        updatedAlbumFileRes = await unlike(file, yourLike)
      } else {
        updatedAlbumFileRes = await like(file)
      }
      const updatedAlbumFile: AlbumFile = updatedAlbumFileRes.data
      console.log('updatedFile:', updatedAlbumFile)
      updateFile(updatedAlbumFile)
    } catch (e) {
      captureException(e, 'liking comment')
      console.error(e)
      alert('Error deleting file: ' + JSON.stringify(e))
    } finally {
      setIsLiking(false)
    }
  }

  function onEditDescClick() {
    setIsEditingDescription(true)
    setDescriptionContent(currentFile.description || '')
  }

  async function onCommentPostClick() {
    if (isCommenting) {
      return
    }

    setIsCommenting(true)

    try {
      const albumFileRes = await comment(currentFile, commentContent)
      const albumFile: AlbumFile = albumFileRes.data

      updateFile(albumFile)
      setCommentContent('')
    } catch (e) {
      captureException(e, 'posting comment')
      console.error('error posting comment')
      alert('Error posting comment: ' + JSON.stringify(e))
    } finally {
      setIsCommenting(false)
    }
  }

  async function onUpdateDescriptionClick(e: any) {
    e.preventDefault()
    e.stopPropagation()

    if (isDescribing) {
      return
    }

    setIsDescribing(true)

    try {
      const albumFileRes = await fileDescription(currentFile, descriptionContent)
      const albumFile: AlbumFile = albumFileRes.data

      updateFile(albumFile)
      setDescriptionContent('')
      setIsEditingDescription(false)
    } catch (e) {
      captureException(e, 'setting description')
      console.error('error posting comment')
      alert('Error setting description: ' + JSON.stringify(e))
    } finally {
      setIsDescribing(false)
    }
  }

  function getShareData(): ShareDownloadData {
    if (!currentFile) {
      return NO_SHARE
    }

    if (!navigator.share) {
      return NO_SHARE
    }

    let decision: ShareDownloadDataDecision
    if (currentFile.size <= SHARE_DOWNLOAD_LIMIT) {
      decision = ShareDownloadDataDecision.USE_ORIGINAL
    } else if (currentFile.detail_size && currentFile.detail_size <= SHARE_DOWNLOAD_LIMIT) {
      decision = ShareDownloadDataDecision.USE_DETAIL
    } else {
      decision = ShareDownloadDataDecision.TOO_LARGE_USE_URL
    }

    return {
      canShare: true,
      decision,
    }
  }

  const shareData = getShareData()

  // Which variant the download button hands out. In share/portfolio mode the drive store
  // may hold an unrelated drive, so only the prop counts there (and the watermark is
  // unknown — restrict such galleries via download_options: 'detail').
  const downloadQuality = resolveSingleFileQuality(
    shareMode
      ? resolveDownloadQualities({ downloadOptions })
      : resolveDownloadQualities({ downloadOptions: downloadOptions ?? currentDrive?.download_options, hasWatermark: !!currentDrive?.watermark }),
    currentDrive?.use_details_dl,
  )

  async function onDownloadClick() {
    if (!currentFile) {
      return
    }

    if (downloadQuality === 'high' || currentFile.is_demo) {
      // Detail-only: hand out the web-optimized version on *every* path, including the
      // iOS Web-Share branch (which shares the first argument, not the alternate link).
      const detailUrl = currentFile.detail ?? currentFile.file
      const iOS = /(iPad|iPhone|iPod)/g.test(navigator.userAgent);
      if (iOS) {
        setIsShowiOSDownloadHint(true)
      }
      try {
        await shareDownloadURL(detailUrl, currentFile.file_name, setIsDownloading, detailUrl)
      } finally {
        setIsShowiOSDownloadHint(false)
      }
      return
    }

    if (currentFile.size > SHARE_DOWNLOAD_LIMIT) {
      await saveAs(`${import.meta.env.VITE_PUBLIC_API_URL}/api/files/${currentFile.id}/download/`, currentFile.file_name, setIsDownloading)
    } else {
      const iOS = /(iPad|iPhone|iPod)/g.test(navigator.userAgent);
      if (iOS) {
        setIsShowiOSDownloadHint(true)
      }
      try {
        await shareDownloadURL(currentFile.file, currentFile.file_name, setIsDownloading, `${import.meta.env.VITE_PUBLIC_API_URL}/api/files/${currentFile.id}/download/`)
      } finally {
        if (iOS) {
          setIsShowiOSDownloadHint(false)
        }
      }
    }
  }

  async function onShareClick() {
    if (!shareData.canShare || !currentFile) {
      return
    }

    // Sharing the image itself — nothing is stored, so no API call counts it.
    track('file_shared', { album: selectionAlbumId, file: currentFile.id })

    if (shareData.decision != ShareDownloadDataDecision.TOO_LARGE_USE_URL) {
      const downloadUrl = shareData.decision == ShareDownloadDataDecision.USE_DETAIL ? currentFile.detail : currentFile.file
      await shareDownloadedFileByUrl(downloadUrl!, currentFile.file_name, setIsSharing)
    } else {
      await navigator.share({
        url: currentFile.file,
      })
    }
  }

  async function onCommentClick() {
    if (currentFile.is_demo) {
      return alert(t('common.onlyInFullVersion'))
    }

    commentModalRef.current!.showModal()
  }

  const likeCount = currentFile.likes ? currentFile.likes.length : 0
  const yourLike = currentFile.likes && profile ? currentFile.likes.find(file => file.author === profile.username) : null
  // On prof drives the heart is a selection toggle, not a real like.
  const isSelectedInSelection = selectionAsLike && fileSelection ? fileSelection.isFileSelected(currentFile, selectionAlbumId) : false

  const commentsCount = currentFile.comments ? currentFile.comments.length : 0

  const hideDownload = !!currentDrive?.is_hide_download
  const hideShare = !!currentDrive?.is_hide_share

  const actionButtons = [
    !hideDownload ? <Button className='text-[white] opacity-85 shadow' size='sm' color='ghost' key='download-file' onClick={onDownloadClick}>
      { isDownloading && <Loading color='primary' />}
      { !isDownloading && <IconDownload /> }
    </Button> : null,
      !hideShare && shareData.canShare ? <Button className='text-[white] opacity-85 shadow' size='sm' color='ghost' key='share-file' onClick={onShareClick}>
        { isSharing && <Loading color='primary' /> }
        { !isSharing && <IconShare2 /> }
      </Button> : null,
    !shareMode ? <Button className='text-[white] opacity-85 shadow' size='sm' color='ghost' key='comment-file' onClick={onCommentClick}>
      <div className='flex flex-row justify-center items-center gap-2'>
        { commentsCount != 0 && <span className='text-lg'>{commentsCount}</span>}
        <IconMessage />
      </div>
    </Button> : null,
    selectionAsLike
      ? (fileSelection ? <Button className='text-[white] opacity-85 shadow' size='sm' color='ghost' key='select-file' title='Auswählen' onClick={() => fileSelection.toggleFileInSelection(currentFile, selectionAlbumId)}>
          { isSelectedInSelection ? <IconHeartFilled /> : <IconHeart /> }
        </Button> : null)
      : (!shareMode && (likeCount != 0 || profile != null) ? <Button className='text-[white] opacity-85 shadow' size='sm' color='ghost' key='like-file' onClick={onLikeClick}>
          <div className='flex flex-row justify-center items-center gap-2'>
            { likeCount != 0 && <span className='text-lg'>{likeCount}</span> }
            { isLiking && <Loading />}
            { !isLiking && (yourLike ? <IconHeartFilled /> : <IconHeart />)}
          </div>
        </Button> : null),
  ]

  if (!shareMode && profile && currentDrive && hasPermissionsDeleteFile(currentFile, currentDrive, profile)) {
    if (currentFile.is_demo) {
      // toolbarButtons.unshift(
      //   <div color='primary' key='demo' className='flex h-full bg-primary'>Demo Beispiel</div>
      // )
    } else {
      actionButtons.push(
        <Button size='sm' color='ghost' key='delete-file' onClick={onDeleteClick}>
          <IconTrash />
        </Button>
      )
    }
  }

  const descriptionText = currentFile.is_demo ?
    `${new Date(currentDrive ? currentDrive.created_at : Date.now()).toLocaleDateString()}, ${new Date(currentDrive ? currentDrive.created_at : Date.now()).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}` :
    formatFileDateTime(currentFile)

  // Explicit caption from the drive settings wins over the drive-type default
  // (normal: uploader + date, prof: nothing). In share mode the drive store may hold an
  // unrelated drive, so only the prop counts there.
  const captionSetting = openedImageCaption ?? (shareMode ? '' : currentDrive?.opened_image_caption)
  const captionOverride = buildOpenedImageCaption(currentFile, captionSetting)

  return <>
    <Modal ref={deleteModalRef} responsive className='bg-neutral'>
      <Modal.Header>
        {t('common.deleteQst')}
        <form method="dialog">
          <button disabled={isDeleting} className="btn btn-sm btn-circle btn-ghost absolute right-5 top-5">✕</button>
        </form>
      </Modal.Header>
      <Modal.Body>
        <div className='text-center flex flex-col gap-5'>
          <p>
            {t('common.deleteRly')}
          </p>
          <div className='flex flex-col gap-3'>
            <Button className='text-[#FFFFFF]' onClick={onDeleteConfirm} color='error' disabled={isDeleting} loading={isDeleting}>
              {t('common.delete')}
            </Button>
            <form className='w-full' method="dialog">
              <Button wide color='ghost' disabled={isDeleting}>
                {t('common.cancel')}
              </Button>
            </form>
          </div>
        </div>
      </Modal.Body>
    </Modal>

    <Modal ref={commentModalRef} responsive className='bg-neutral'>
      <Modal.Body>
        <form method="dialog">
          <button disabled={isCommenting} className="btn btn-sm btn-circle btn-ghost absolute right-5 top-5">
            <IconX />
          </button>
        </form>
        <div className='w-full bg-neutral px-4 md:px-[2rem] pb-5 rounded-t-2xl h-[35vh] max-h-[20rem] pointer-events-auto'>
          {
            !currentFile.description && currentFile.author == profile?.username && !isEditingDescription && <>
              <Button color='primary' className='mb-5' onClick={onEditDescClick}><IconPencil /> {t('common.description')}</Button>
            </>
          }
          {
            isEditingDescription && <>
              <form onSubmit={onUpdateDescriptionClick}>
                <div className='w-full'>
                  <Input disabled={isDescribing} className='w-full bg-neutral border-primary' maxLength={500} value={descriptionContent} onChange={e => setDescriptionContent(e.target.value)} />
                </div>
                <Button loading={isDescribing} disabled={isDescribing} className='my-3' color='primary'><IconCheck /> {t('common.description')}</Button>
              </form>
            </>
          }
          { currentFile.description && !isEditingDescription && <>
            <div className='flex flex-row gap-3 items-center mb-2'>
              <p className='text-xl'>{t('common.description')}</p>
              { profile && currentFile.author == profile.username || (currentDrive && profile && currentDrive.author == profile.username) }
              <Button size='sm' color='primary' onClick={onEditDescClick}><IconPencil /></Button>
            </div>
            <p className='mb-5'>{currentFile.description}</p>
          </>}
          <p className='text-xl'>{t('common.comments')} ({commentsCount})</p>
          <div className='w-full'>
            <div className='flex flex-col h-full w-full'>
              <div className='w-full max-h-[20vh] overflow-scroll'>
                { currentFile.comments && currentFile.comments.map(comment => <AlbumFileCommentElement key={comment.id} file={currentFile} comment={comment} drive={currentDrive} profile={profile} updateFile={updateFile} />)}
              </div>
              <div className='flex-1'></div>
              {
                profile && <form>
                  <div className='w-full'>
                    <Input disabled={isCommenting} className='w-full bg-neutral border-primary' value={commentContent} onChange={e => setCommentContent(e.target.value)} />
                  </div>
                  <Button fullWidth loading={isCommenting} disabled={isCommenting || !commentContent} className='my-3' color='primary' onClick={onCommentPostClick}>{t('common.postComment')}</Button>
                </form>
              }
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
    <div className='fixed top-0 left-0 w-full h-full m-0 p-0 max-w-full max-h-full z-[9999]'>
      <Lightbox
        className='m-0 p-0'
        open={true}
        slides={slides}
        index={selectedFileIndex}
        close={() => onSelectFileIndex(null)}
        on={{
          view: ({ index }) => onSelectFileIndex(index),
        }}
        toolbar={{
          // buttons: toolbarButtons,
        }}
        plugins={[
          Inline,
          Video,
          // Download,
          // Share,
          // MyCaptions,
          Zoom,
        ]}
        render={{
          buttonZoom: () => null,
        }}
      />
      { currentFile && <div className='absolute bottom-0 right-0 text-neutral w-full flex flex-col h-full pointer-events-none'>
        { isShowiOSDownloadHint && <div className='absolute left-[50%] w-[80%] top-[3rem] shadow bg-[white] p-2 rounded-2xl' style={{transform: 'translateX(-50%)'}}>
          <img className='rounded-xl' src='/save-hint-download.webp' />
        </div>}
        <div className='w-full flex flex-row justify-end pt-3'>
          <Button className='pointer-events-auto text-[white] opacity-85' size='sm' color='ghost' onClick={() => onSelectFileIndex(null)}><IconCircleX size={30} /></Button>
        </div>
        <div className='flex flex-row w-full mt-auto'>
          <div className='flex-1 px-2 text-[white] mt-auto'>
            <span className='opacity-85'>
              {currentFile.description}
            </span>
            {currentFile.game && slides[selectedFileIndex].drive && <p className='font-bold flex items-center'>{currentFile.game.challenge}
              <Link className='pointer-events-auto ms-3' to={`/d/${slides[selectedFileIndex].drive.id}/${currentFile.game.album}/game/${currentFile.game.id}`}>
                <Button color='primary' size='sm'>{t('common.games')}</Button>
              </Link>
            </p>}
          </div>
          <div className='mt-auto ms-auto flex flex-col gap-1 text-error items-end pointer-events-auto'>
            {actionButtons}
          </div>
        </div>
        { captionOverride
          ? <div className='text-[white] opacity-50 ps-2 pe-4 py-2 flex justify-start text-[1.1rem] break-all'>
              {captionOverride}
            </div>
          : showAuthorInfo && <div className='text-[white] opacity-50 ps-2 pe-4 py-2 flex justify-start text-[1.1rem]'>
              {currentFile.author_display_name} {descriptionText}
            </div> }
      </div>}
    </div>
  </>
}

function AlbumFileCommentElement({ file, comment, profile, drive, updateFile }: { file: AlbumFile, comment: AlbumFileComment, profile: HeyProfile | null, drive: HeyDrive | null, updateFile: (file: AlbumFile) => void }) {
  const canDeleteComment = profile && drive && hasPermissionsDeleteComment(comment, drive, profile)
  const [showDelete, setShowDelete] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const { deleteComment } = useClient()
  const { t } = useTranslation()

  async function onDeleteConfirm() {
    if (isDeleting) {
      return
    }

    setIsDeleting(true)
    try {
      const fileRes = await deleteComment(file, comment)
      const updatedFile: AlbumFile = fileRes.data
      setShowDelete(false)
      updateFile(updatedFile)
    } catch (e) {
      captureException(e, 'deleting comment')
      console.error(e)
      alert('Error deleting comment: ' + JSON.stringify(e))
    } finally {
      setIsDeleting(false)
    }
  }

  return <div className='flex flex-row items-center'>
    <div className='flex flex-col w-full mb-3'>
      <div>
        <span className='text-sm opacity-50 font-bold'>{comment.author_display_name}</span>
      </div>
      <span className='py-0 flex-1'>
        {comment.content}
      </span>
    </div>
    {
      canDeleteComment && <Button className='border-0' color='primary' variant='outline' size='sm' onClick={() => setShowDelete(true)}>
        <IconTrash />
      </Button>
    }
    {
      showDelete && <Modal open>
        <Modal.Header>
          {t('common.delete')}
          <button disabled={isDeleting} className="btn btn-sm btn-circle btn-ghost absolute right-5 top-5" onClick={() => setShowDelete(false)}>✕</button>
          <div className='flex flex-col gap-3 my-5'>
            <Button disabled={isDeleting} loading={isDeleting} color='error' onClick={onDeleteConfirm}>{t('common.delete')}</Button>
            <Button disabled={isDeleting} color='primary' variant='outline' onClick={() => setShowDelete(false)}>{t('common.cancel')}</Button>
          </div>
        </Modal.Header>
      </Modal>
    }
  </div>
}
